import { Fragment } from 'react'

import { track } from '@onl-web/shared/analytics'
import { type OpenBottomType, useBridge } from '@onl-web/shared/bridge'

import quizDisabledIcon from '@/assets/icons/disabled/quiz-disabled.png'
import shareDisabledIcon from '@/assets/icons/disabled/share-disabled.png'
import sugeoDisabledIcon from '@/assets/icons/disabled/sugeo-disabled.png'
import questionIcon from '@/assets/icons/quiz.png'
import shareIcon from '@/assets/icons/share.png'
import sugeolIcon from '@/assets/icons/sugeo.png'
import { type CompletionToastVariant, isCompletionToastVariant } from '@/components/CompletionToast/utils'
import { useDevQueryLog } from '@/hooks/useDevLog'
import { SectionTitle } from '@/pages/gacha/steps/CouponStep/components/shared/styles'
import { useGetMissions } from '@/query/mission/useGetMissions'
import { useGetQuizzes } from '@/query/quiz/useGetQuizzes'

import MissionItem from './components/MissionItem'
import { Card, Container, Divider } from './styles'

type MissionKey = 'payment' | 'share' | 'quiz'

interface MissionConfig {
  key: MissionKey
  iconSrc: string
  disabledIconSrc: string
  title: string
  description: string
  secondDescription?: string
}

const MISSIONS: MissionConfig[] = [
  {
    key: 'payment',
    iconSrc: sugeolIcon,
    disabledIconSrc: sugeoDisabledIcon,
    title: '수거 완료하기',
    description: '결제 성공 시 1',
  },
  {
    key: 'share',
    iconSrc: shareIcon,
    disabledIconSrc: shareDisabledIcon,
    title: '친구에게 공유하기',
    description: '1일 1',
  },
  {
    key: 'quiz',
    iconSrc: questionIcon,
    disabledIconSrc: quizDisabledIcon,
    title: '퀴즈 참여하기',
    description: '1일 1',
    secondDescription: '• 월 최대 10',
  },
]

interface MissionSectionProps {
  onQuiz: () => void
  onMissionComplete?: (type: CompletionToastVariant) => void
}

export default function MissionSection({ onQuiz, onMissionComplete }: MissionSectionProps) {
  const missionsQuery = useGetMissions()
  const missions = missionsQuery.data
  const quizzesQuery = useGetQuizzes()
  const isQuizMonthlyLimitReached = quizzesQuery.data
    ? quizzesQuery.data.solvedCount >= quizzesQuery.data.monthlyQuizLimit
    : false
  const { callHandler } = useBridge()

  const openBottomAndNotify = async (type: OpenBottomType) => {
    try {
      const result = await callHandler('openBottom', { type })
      if (isCompletionToastVariant(result.type)) {
        onMissionComplete?.(result.type)
      }
    } catch {
      // 사용자 취소/브릿지 실패는 토스트 띄우지 않음
    }
  }

  const openBottomAndQuiz = async () => {
    try {
      const result = await callHandler('openBottom', { type: 'quiz' })
      if (!result) return
      if (isCompletionToastVariant(result.type)) {
        onMissionComplete?.(result.type)
      }
      onQuiz()
    } catch {
      // 사용자 취소/브릿지 실패 시 퀴즈 진입 안 함
    }
  }

  const handleMissionAction = (mission: MissionConfig) => {
    track('click_gacha_receive_btn', { btn_name: mission.title })

    if (mission.key === 'quiz') {
      void openBottomAndQuiz()
      return
    }
    if (mission.key === 'share') {
      openBottomAndNotify('share')
      return
    }
    if (mission.key === 'payment') {
      openBottomAndNotify('order')
    }
  }

  useDevQueryLog('missions', missionsQuery)

  return (
    <Container>
      <SectionTitle>에너지 얻는 방법</SectionTitle>
      <Card>
        {MISSIONS.map((mission, index) => {
          const isTodayCompleted = missions?.[mission.key]?.isTodayCompleted ?? false
          const isCompleted =
            mission.key === 'quiz' ? isTodayCompleted || isQuizMonthlyLimitReached : isTodayCompleted

          return (
            <Fragment key={mission.key}>
              {index > 0 && <Divider />}
              <MissionItem
                iconSrc={isCompleted ? mission.disabledIconSrc : mission.iconSrc}
                title={mission.title}
                description={mission.description}
                secondDescription={mission.secondDescription}
                isCompleted={isCompleted}
                onAction={() => handleMissionAction(mission)}
              />
            </Fragment>
          )
        })}
      </Card>
    </Container>
  )
}
